class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    if (this.header) this.header.preventHide = false;
    this.lastWishlistKey = '';
    this.renderRequestId = 0;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.querySelector('#WishlistDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderWishlistAccessibility();
  }

  setHeaderWishlistAccessibility() {
    const wishlistButton = document.querySelector('#wishlist-icon-bubble');
    if (!wishlistButton) return;

    wishlistButton.setAttribute('role', 'button');
    wishlistButton.setAttribute('aria-haspopup', 'dialog');
    wishlistButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(wishlistButton);
    });
    wishlistButton.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(wishlistButton);
      }
    });
    const warmup = () => this.renderContents({ preferCached: true });
    wishlistButton.addEventListener('mouseenter', warmup, { once: true });
    wishlistButton.addEventListener('focus', warmup, { once: true });
  }

  open(triggeredBy) {
    if (this.header) this.header.preventHide = true;
    if (triggeredBy) this.activeElement = triggeredBy;
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener(
      'transitionend',
      () => {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__empty')
          : document.getElementById('WishlistDrawer');
        const focusTarget = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (container && focusTarget) trapFocus(container, focusTarget);
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden-drawer');
    this.renderContents({ preferCached: true });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden-drawer');
    if (this.header) this.header.preventHide = false;
  }

  getWishlist() {
    try {
      const wishlist = JSON.parse(localStorage.getItem('shopify_wishlist') || '[]');
      return Array.isArray(wishlist) ? wishlist : [];
    } catch (error) {
      console.error('Error reading wishlist:', error);
      return [];
    }
  }

  saveWishlist(nextWishlist) {
    localStorage.setItem('shopify_wishlist', JSON.stringify(nextWishlist));
    window.dispatchEvent(new Event('wishlist:updated'));
  }

  removeFromWishlist(handle) {
    const wishlist = this.getWishlist().filter((item) => item !== handle);
    this.saveWishlist(wishlist);
    this.renderContents();
  }

  updateHeaderCount(count) {
    const bubbles = document.querySelectorAll('.wishlist-count');
    bubbles.forEach((bubble) => {
      const visibleCount = bubble.querySelector('[aria-hidden="true"]') || bubble;
      visibleCount.textContent = String(count);
      bubble.style.display = count > 0 ? 'flex' : 'none';
    });
    const drawerCount = document.getElementById('WishlistDrawer-Count');
    if (drawerCount) drawerCount.textContent = String(count);
  }

  async renderContents({ preferCached = false, force = false } = {}) {
    const wishlist = this.getWishlist();
    const itemsRoot = document.getElementById('WishlistDrawer-Items');
    if (!itemsRoot) return;
    const wishlistKey = wishlist.join('|');

    this.updateHeaderCount(wishlist.length);
    this.classList.toggle('is-empty', wishlist.length === 0);
    if (wishlist.length === 0) {
      itemsRoot.innerHTML = '';
      this.lastWishlistKey = '';
      return;
    }

    if (!force && preferCached && this.lastWishlistKey === wishlistKey && itemsRoot.children.length > 0) {
      return;
    }

    const requestId = ++this.renderRequestId;
    if (itemsRoot.children.length === 0 || force) {
      itemsRoot.innerHTML = '<div class="wishlist-drawer__loading"><div class="spinner"></div></div>';
    }

    const entries = await Promise.all(
      wishlist.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}?view=wishlist-card`);
          if (!response.ok) return null;
          const html = await response.text();
          if (!html.trim()) return null;
          return { handle, html };
        } catch (error) {
          console.error('Wishlist item fetch failed:', handle, error);
          return null;
        }
      })
    );

    if (requestId !== this.renderRequestId) return;

    const validEntries = entries.filter(Boolean);
    if (validEntries.length === 0) {
      this.classList.add('is-empty');
      itemsRoot.innerHTML = '';
      this.updateHeaderCount(0);
      this.lastWishlistKey = '';
      return;
    }

    const removeLabel = this.dataset.removeLabel || 'Remove';
    const addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';

    itemsRoot.innerHTML = validEntries
      .map(
        (entry) => `
          <div class="wishlist-drawer__item" data-wishlist-handle="${entry.handle}">
            <div class="wishlist-drawer__item-header">
              ${entry.html}
              <button class="wishlist-drawer__remove focus-inset" type="button" aria-label="${removeLabel}">
                <svg class="icon icon-delete" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.8077 17.4423C2.30898 17.4423 1.88302 17.2657 1.52982 16.9125C1.17661 16.5593 1 16.1333 1 15.6346V2.94231H0V1.44234H4.49997V0.557739H10.5V1.44234H15V2.94231H14V15.6346C14 16.1397 13.825 16.5673 13.475 16.9173C13.125 17.2673 12.6974 17.4423 12.1922 17.4423H2.8077ZM12.5 2.94231H2.49997V15.6346C2.49997 15.7243 2.52883 15.7981 2.58652 15.8558C2.64423 15.9135 2.71795 15.9423 2.8077 15.9423H12.1922C12.2692 15.9423 12.3397 15.9103 12.4038 15.8462C12.4679 15.782 12.5 15.7115 12.5 15.6346V2.94231ZM4.90385 13.9423H6.40382V4.94231H4.90385V13.9423ZM8.59613 13.9423H10.0961V4.94231H8.59613V13.9423Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        `
      )
      .join('');

    itemsRoot.querySelectorAll('.wishlist-drawer__remove').forEach((button) => {
      button.addEventListener('click', () => {
        const parent = button.closest('[data-wishlist-handle]');
        if (!parent) return;
        this.removeFromWishlist(parent.getAttribute('data-wishlist-handle'));
      });
    });

    // Ensure each item has an Add to cart button (fallback)
    await Promise.all(
      validEntries.map(async (entry) => {
        const itemEl = itemsRoot.querySelector(`[data-wishlist-handle="${entry.handle}"]`);
        if (!itemEl) return;

        const alreadyHasAdd =
          itemEl.querySelector('form[action*="/cart/add"]') ||
          itemEl.querySelector('.card__more.button') ||
          itemEl.querySelector('button[name="add"]');

        if (alreadyHasAdd) return;

        let productJson;
        try {
          const res = await fetch(`/products/${entry.handle}.js`, { credentials: 'same-origin' });
          if (!res.ok) return;
          productJson = await res.json();
        } catch (e) {
          return;
        }

        const variant =
          (productJson.variants || []).find((v) => v.available) || (productJson.variants || [])[0];
        if (!variant) return;

        const target =
          itemEl.querySelector('.card__info-group') ||
          itemEl.querySelector('.card-information') ||
          itemEl;

        const wrapper = document.createElement('div');
        wrapper.className = 'wishlist-drawer__fallback-atc';
        wrapper.innerHTML = `
          <form method="post" action="/cart/add" class="wishlist-drawer__atc-form">
            <input type="hidden" name="id" value="${variant.id}">
            <button type="submit" name="add" class="card__more button button--simple">
              <span>${addToCartLabel}</span>
            </button>
          </form>
        `;
        target.appendChild(wrapper);
      })
    );

    this.lastWishlistKey = wishlistKey;
  }
}

if (!customElements.get('wishlist-drawer')) {
  customElements.define('wishlist-drawer', WishlistDrawer);
}

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('wishlist-drawer');
  if (drawer) drawer.renderContents({ preferCached: true });
});

window.addEventListener('wishlist:updated', () => {
  const drawer = document.querySelector('wishlist-drawer');
  if (drawer) drawer.renderContents({ force: true });
});
